import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'cw_session_token';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = React.useRef(null);

  const saveToken = (token) => {
    tokenRef.current = token;
    if (token) {
      try { localStorage.setItem(TOKEN_KEY, token); } catch { /* private browsing */ }
    }
  };

  const clearToken = () => {
    tokenRef.current = null;
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* private browsing */ }
  };

  // Restore token from localStorage on mount
  if (!tokenRef.current) {
    try { tokenRef.current = localStorage.getItem(TOKEN_KEY); } catch { /* private browsing */ }
  }

  const api = React.useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      withCredentials: true,
    });
    instance.interceptors.request.use((config) => {
      if (tokenRef.current) {
        config.headers.Authorization = `Bearer ${tokenRef.current}`;
      }
      return config;
    });
    return instance;
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      if (response.data.token) {
        saveToken(response.data.token);
      }
      setUser(response.data);
    } catch {
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = useCallback(async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    saveToken(response.data.token);
    try {
      const meRes = await api.get('/auth/me');
      setUser(meRes.data);
    } catch {
      setUser(response.data.user);
    }
    return response.data.user;
  }, [api]);

  const register = useCallback(async (userData) => {
    const response = await api.post('/auth/register', userData);
    // New flow: register does NOT auto-login. User must verify email + be approved.
    return response.data;
  }, [api]);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Proceed even if server call fails
    }
    clearToken();
    setUser(null);
  }, [api]);

  const updateProfile = useCallback(async (updates) => {
    const response = await api.put('/auth/profile', updates);
    setUser(response.data);
    return response.data;
  }, [api]);

  const isAdmin = user?.role === 'admin';
  const isModerator = user?.role === 'moderator' || user?.role === 'admin';

  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    isAdmin,
    isModerator,
    api
  }), [user, loading, login, register, logout, updateProfile, isAdmin, isModerator, api]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
